/*
 * Developer  : Sakshi Bhise
 * Script     : Sidebarlayout.style.ts
 * Date       : 2026-06-08
 * Purpose    : Defines all the visual styles (colours, spacing, fonts, layout rules)
 *              used by the SidebarLayout component and its child panels.
 *              Styles are written as JavaScript objects (CSS-in-JS) so they can
 *              read live colour values from the active Fluent UI theme.
 */

// ─── Imports ──────────────────────────────────────────────────────────────────

import type { IStyle, ITheme } from "@fluentui/react"; // Fluent UI type definitions: IStyle describes a style object, ITheme describes the colour theme
import { TYPOGRAPHY, COLORS } from '@teva/ui';         // Teva design tokens: TYPOGRAPHY holds font settings, COLORS holds the brand colour palette

// ─── Function: getSidebarLayoutStyles ────────────────────────────────────────

/**
 * Builds and returns all CSS-in-JS style rules for the SidebarLayout.
 * Accepts the current theme so colours automatically update in light and dark mode.
 *
 * @param theme - The active Fluent UI theme, used to source palette colours.
 * @returns A map of named style objects covering navigation, settings, and main content layout.
 */
export const getSidebarLayoutStyles = (theme: ITheme) => ({

    // ─── Styles: Navigation Menu ──────────────────────────────────────────────

    // Outer wrapper for the entire app — ensures it fills the browser window
    root: {
        fontFamily: TYPOGRAPHY.fontFamily.base, // sets the default font for every text element in the app
        height: "100%",                         // fills the full height of the parent container
    } as IStyle,

    // A single clickable row in the sidebar navigation (e.g. the Chat History row)
    menuItem: {
        display: "flex",                               // places the icon and label side by side
        alignItems: "center",                          // vertically centres the icon and text within the row
        gap: 10,                                       // horizontal gap between the icon and the label
        padding: "6px 12px",
        height: "calc(2 * 1.3em + 12px)",   // fixed height for every row — same box whether text is 1 or 2 lines, so hover's bold weight never changes row size
        margin: "0 8px",                               // small gap between the row and the sidebar edges
        borderRadius: 6,                               // softly rounded corners
        cursor: "pointer",                             // hand cursor so users know the row is clickable
        color: theme.palette.neutralPrimary,           // default text and icon colour from the theme
        background: "transparent",                     // no background fill unless hovered or active
        border: "none",                                // removes the default browser button outline
        width: "calc(100% - 16px)",                    // full sidebar width minus the 8px margin on each side
        textAlign: "left" as const,                    // left-aligns the label text inside the row
        transition: "background 0.15s, color 0.15s",  // smooth colour change when the row is hovered
        selectors: {
            ":hover": {
                background: theme.palette.neutralLighter, // light grey fill on hover
                color: theme.palette.neutralDark,         // slightly darker text on hover for better contrast
            },
            ":hover span:last-child": {
                fontWeight: 700, // makes the label text bold when the row is hovered
            },
        },
    } as IStyle,

    // Overrides applied to a nav row when the sidebar is in its narrow/icon-only state
    menuItemCollapsed: {
        justifyContent: "center", // centres the icon horizontally since there is no label to place beside it
        padding: "6px 0",         // removes horizontal padding — the icon fills the narrow sidebar
        width: "100%",            // expands to the full sidebar width
        margin: 0,                // removes the side margins used in the expanded state
    } as IStyle,

    // Applied to whichever nav row's panel is currently open
    menuItemActive: {
        background: theme.palette.neutralLighter, // same fill as hover — marks this row as selected
        color: theme.palette.neutralDark,         // darker text so the active row stands out
    } as IStyle,

    // The icon wrapper inside each nav row
    menuItemIcon: {
        display: "inline-flex",  // allows the icon to sit inline while still using flex alignment
        alignItems: "center",    // vertically centres the icon within its wrapper
        justifyContent: "center",// horizontally centres the icon within its wrapper
        flexShrink: 0,           // prevents the icon from shrinking when the row is narrow
        color: "inherit",        // inherits the row's text colour (changes on hover/active)
    } as IStyle,

    // The label text beside the icon in each nav row
    menuItemText: {
        fontSize: 13.5,                            // slightly smaller than body text for a compact sidebar feel
        fontFamily: TYPOGRAPHY.fontFamily.base,
        fontWeight: 400,                           // normal weight by default
        lineHeight: "1.3em",                           // comfortable line spacing across 2 lines
        display: "-webkit-box" as const,           // required by -webkit-line-clamp to cap the text at a fixed number of lines
        WebkitLineClamp: 2,                        // shows up to 2 full lines before truncating
        WebkitBoxOrient: "vertical" as const,       // required companion property for -webkit-line-clamp
        overflow: "hidden" as const,                // clips anything beyond the 2 allowed lines
        textOverflow: "ellipsis",                   // shows "…" only if text is too long even for 2 lines
        color: "inherit",                           // picks up the row's colour (changes on hover/active)
    } as IStyle,

    // Additional style applied to the label when its row is active
    menuItemTextActive: {
        fontWeight: 700, // bold label when this nav item's panel is open
    } as IStyle,

    // ─── Styles: Notification Footer ──────────────────────────────────────────

    // "Mark all read" button at the bottom of the notifications panel
    markAllReadButton: {
        width: '100%',                       // spans the full panel width
        padding: '10px 16px',                // comfortable tap target
        border: 'none',                      // no border — blends into the panel
        background: 'transparent',           // no background fill
        cursor: 'pointer',                   // hand cursor signals interactivity
        fontSize: 13,                        // matches the settings row label size
        color: COLORS.brand.primary,         // brand blue/violet to signal a primary action
        fontWeight: 500,                     // medium weight for the action label
        textAlign: 'center',                 // centres the "Mark all read" text horizontally
        selectors: {
            ':hover': {
                background: COLORS.blumine[50], // faint tint on hover to confirm the button is interactive
            },
        },
    } as IStyle,

    // ─── Styles: Main Content Area ────────────────────────────────────────────

    // The box that wraps content inside panel sections (e.g. the Workspaces placeholder)
    workspaceContent: {
        padding: '16px', // standard inner padding
    } as IStyle,

    // The centre column that holds the active chat page
    mainContent: {
        flex: 1,              // grows to fill all available space left after the sidebar
        minWidth: 0,          // allows this column to shrink when the secondary panel expands (without this, flex items refuse to shrink below their content size)
        overflow: 'hidden',   // clips content rather than allowing it to overflow the viewport
        display: 'flex',      // stacks child sections vertically
        flexDirection: 'column',
        gap: '16px',          // consistent vertical spacing between stacked sections
    } as IStyle,

    // A horizontal row of action buttons (used in the omnibox area)
    buttonsRow: {
        display: 'flex', // places buttons side by side
        gap: '8px',      // small gap between each button
    } as IStyle,

    // Wrapper that positions the chat input (omnibox) at the bottom of the content area
    omniboxContainer: {
        flex: 1,                  // fills all vertical space above the input
        display: 'flex',
        justifyContent: 'center', // centres the input horizontally
        alignItems: 'flex-end',   // pins the input to the bottom of its container
        width: '100%',
    } as IStyle,

    // Constrains the input to a comfortable reading width
    omniboxMotionWrapper: {
        width: '100%',
        maxWidth: '940px', // caps the input width so long lines remain easy to read
    } as IStyle,

    // Inner wrapper needed by animation libraries that require a relative-positioned parent
    omniboxInner: {
        position: 'relative', // lets absolutely positioned children (e.g. a floating toolbar) anchor to this box
        width: '100%',
    } as IStyle,

    // ─── Styles: Settings Panel ───────────────────────────────────────────────

    // Small uppercase heading above each section in the Settings panel (e.g. "GENERAL", "PRIVACY & DATA")
    settingsSectionLabel: {
        display: 'block',                                     // forces the heading onto its own line
        fontSize: 10,                                         // small-caps style size
        fontWeight: 700,                                      // bold so it reads as a heading
        color: theme.palette.neutralTertiaryAlt,              // muted colour so it does not compete with the content below
        margin: '32px 16px 4px',                              // extra top margin creates visual breathing room between sections
        textTransform: 'uppercase',                           // all-caps style typical of section labels
        letterSpacing: '1.12px',                              // wide letter spacing reinforces the all-caps look
    } as IStyle,

    // Vertical stack that holds the "Appearance" label and the light/dark/system picker
    settingsAppearanceRow: {
        display: 'flex',
        flexDirection: 'column', // stacks the label above the picker
        gap: 10,                 // space between the label and the picker buttons
        padding: '12px 16px',   // inner padding so content does not touch the row edges
    } as IStyle,

    // The small label ("Appearance", "Language") inside a settings row
    settingsAppearanceLabel: {
        fontSize: 13,                             // compact size for secondary labels
        fontWeight: 500,                          // medium weight — readable but not dominant
        color: theme.palette.neutralPrimaryAlt,   // slightly muted colour for secondary text
    } as IStyle,

    // Outer container for a full settings section (used for sections that have a top-level column layout)
    settingsRow: {
        display: 'flex',
        flexDirection: 'column',           // stacks child rows top to bottom
        justifyContent: 'space-between',
        alignItems: 'flex-start`',         // aligns children to the left edge
        borderBottom: `1px solid ${COLORS.surface.subtle}`, // thin line separating this section from the next
    } as IStyle,

    // A single settings row where the control is top-aligned with the label (used for multi-line labels)
    settingsRowAlignStart: {
        display: 'flex',
        justifyContent: 'space-between',                     // pushes the control to the right
        alignItems: 'flex-start',                            // aligns both sides to the top
        padding: '12px 16px',
        borderBottom: `1px solid ${COLORS.surface.subtle}`, // thin divider below the row
    } as IStyle,

    // The language settings row — stacks the language label row above the search list
    settingsLanguageRow: {
        display: 'flex',
        flexDirection: 'column', // label row on top, search list below when expanded
        gap: 10,
        padding: '12px 16px',
        borderBottom: '1px solid ${COLORS.surface.subtle}' // thin divider (note: template literal not evaluated — kept as-is from original)
    } as IStyle,

    // The header row inside the language section showing the label on the left and the current language on the right
    settingsLanguageSecondRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between', // label on the left, current language name on the right
        alignItems: 'center'             // vertically centre both sides
    } as IStyle,

    // A settings row where the label and control are vertically centred (used for single-line labels)
    settingsRowAlignCenter: {
        display: 'flex',
        justifyContent: 'space-between',                     // pushes the control to the right
        alignItems: 'center',                                // vertically centres both sides
        padding: '12px 16px',
        borderBottom: `1px solid ${COLORS.surface.subtle}`, // thin divider below the row
    } as IStyle,

    // The main title line inside the "Clear all chat history" row
    settingsChatHistoryTitle: {
        fontSize: 13,                           // same size as other setting row labels for visual consistency
        color: theme.palette.neutralPrimaryAlt, // slightly muted primary text colour
        fontWeight: 500,                        // medium weight
        marginBottom: 3,                        // small gap between title and subtitle below
    } as IStyle,

    // The smaller descriptive text beneath the chat history title ("Permanently remove all conversation history")
    settingsChatHistorySubtitle: {
        fontSize: 12,                              // smaller than the title to show it is secondary information
        color: theme.palette.neutralTertiaryAlt,   // muted colour — this is supplementary text
        fontWeight: 400,                           // normal weight
    } as IStyle,

    // Wrapper around the mini recent-conversations list shown under the Chat History nav item
    recentChatHistoryContainer: {
        margin: "0 8px 4px", // small horizontal margin to match the nav item indentation; 4px bottom gap before the next item
        overflow: "hidden",  // clips the list if it somehow overflows its container
    } as IStyle,

    // The red "Delete" button in the Chat History settings row
    deleteButtonStyles: {
        display: "flex",
        alignItems: "center",
        gap: "6px",                                   // space between the bin icon and the label text
        padding: "6px 12px",
        fontSize: "12px",
        fontWeight: 600,                              // bold label for a destructive action
        color: COLORS.status.error,                   // red text (#FB3449) to signal a destructive action
        backgroundColor: "transparent",
        border: `1px solid ${COLORS.status.errorBorder}`, // red border to reinforce the destructive nature
        borderRadius: "8px",
        selectors: {
            ":hover": {
                backgroundColor: COLORS.status.errorSurface,       // faint red tint on hover
                borderColor: COLORS.status.errorBackground1,
                color: COLORS.status.error,                         // keep the red text on hover
            },
            ":active": {
                color: COLORS.status.error, // prevent the text from changing colour when clicked
            },
            ":focus": {
                color: COLORS.status.error, // prevent the text from changing colour when focused via keyboard
            },
        }
    } as IStyle

});
